import { useState } from 'react';
import { Plus, Search, Grid, List, FolderOpen, Loader2, FolderPlus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { sileo } from 'sileo';
import { Header } from '../../widgets/header';
import { Button, Input, Card } from '../../shared/ui';
import { ProjectForm, ProjectCard } from '../../entities/project';
import { useProjects, useDeleteProject } from '../../shared/api';
import { useNavigate } from '@tanstack/react-router';

/**
 * Projects Management Page
 * Fetches real projects from the API, with create project mutation
 */

export function ProjectsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState<any | null>(null);

    // Real API data
    const { data: projects, isLoading, isError } = useProjects();
    const { mutate: deleteProject } = useDeleteProject();

    const cardProjects = (projects ?? []).map((p: any) => ({
        id: p.id,
        name: p.name,
        description: p.description ?? '',
        language: 'TypeScript',
        lastAnalysis: p.updated_at ?? p.created_at,
        healthScore: Number(p.health_score || 85),
        issuesCount: p.issues_count || { critical: 0, high: 0, medium: 0, low: 0 },
        branch: p.default_branch || 'main',
        status: (p.is_active ? 'active' : 'archived') as 'active' | 'archived',
    }));

    // Filter projects
    const filteredProjects = cardProjects.filter((project) =>
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleCreateProject = () => {
        setSelectedProject(null);
        setIsCreateModalOpen(true);
    };

    const handleEditProject = (project: any) => {
        setSelectedProject(project);
        setIsCreateModalOpen(true);
    };

    const handleDeleteProject = (projectId: string, projectName: string) => {
        if (window.confirm(`¿Estás seguro de que deseas eliminar el proyecto "${projectName}"?`)) {
            deleteProject(projectId, {
                onSuccess: () => {
                    sileo.success({
                        title: 'Proyecto Eliminado',
                        description: `El proyecto "${projectName}" ha sido borrado.`,
                        icon: <Trash2 className="size-3.5" />,
                    });
                },
                onError: () => {
                    sileo.error({
                        title: 'Error al eliminar',
                        description: 'No se pudo eliminar el proyecto en este momento.',
                    });
                }
            });
        }
    };

    const handleProjectClick = (projectId: string) => {
        navigate({ to: '/projects/$projectId', params: { projectId } });
    };

    // Safe stats calculation
    const totalProjects = cardProjects.length;
    const criticalCount = cardProjects.reduce((sum, p) => sum + Number(p.issuesCount.critical || 0), 0);
    const avgHealthScore = totalProjects > 0
        ? (cardProjects.reduce((sum, p) => sum + Number(p.healthScore || 0), 0) / totalProjects).toFixed(1)
        : '0.0';
    const activeAnalyses = cardProjects.filter(p => p.status === 'active').length;

    return (
        <div className="min-h-screen bg-background">
            <Header />

            <main className="container mx-auto px-8 py-8 space-y-6">
                {/* Page Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-4xl font-bold text-text-primary font-mono lowercase tracking-tighter">devlint/projects</h1>
                        <p className="text-text-muted font-mono mt-2">
                            {t('projects.subtitle')}
                        </p>
                    </div>
                    <Button
                        variant="primary"
                        corners="asymmetric"
                        icon={<Plus className="h-4 w-4" />}
                        onClick={handleCreateProject}
                    >
                        {t('projects.newProject')}
                    </Button>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card variant="highlighted" padding="md" corners="asymmetric">
                        <div className="space-y-1">
                            <p className="text-sm text-text-secondary font-mono">{t('projects.totalProjects')}</p>
                            <p className="text-3xl font-bold text-primary font-mono">
                                {isLoading ? '...' : totalProjects}
                            </p>
                        </div>
                    </Card>
                    <Card padding="md" corners="asymmetric-inverse">
                        <div className="space-y-1">
                            <p className="text-sm text-text-secondary font-mono">{t('projects.avgHealthScore')}</p>
                            <p className="text-3xl font-bold text-success font-mono">
                                {avgHealthScore}
                            </p>
                        </div>
                    </Card>
                    <Card padding="md" corners="asymmetric">
                        <div className="space-y-1">
                            <p className="text-sm text-text-secondary font-mono">{t('projects.criticalIssues')}</p>
                            <p className="text-3xl font-bold text-error font-mono">
                                {criticalCount}
                            </p>
                        </div>
                    </Card>
                    <Card padding="md" corners="asymmetric-inverse">
                        <div className="space-y-1">
                            <p className="text-sm text-text-secondary font-mono">{t('projects.activeAnalyses')}</p>
                            <p className="text-3xl font-bold text-text-primary font-mono">
                                {activeAnalyses}
                            </p>
                        </div>
                    </Card>
                </div>

                {/* Search and Filters */}
                <div className="flex items-center gap-4">
                    <div className="flex-1">
                        <Input
                            type="search"
                            placeholder={t('projects.searchPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            icon={<Search className="h-4 w-4" />}
                        />
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                            variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                            size="icon"
                            corners="asymmetric"
                            onClick={() => setViewMode('grid')}
                            icon={<Grid className="h-4 w-4" />}
                        />
                        <Button
                            variant={viewMode === 'list' ? 'primary' : 'ghost'}
                            size="icon"
                            corners="asymmetric-inverse"
                            onClick={() => setViewMode('list')}
                            icon={<List className="h-4 w-4" />}
                        />
                    </div>
                </div>

                {/* Loading */}
                {isLoading && (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <span className="ml-3 text-text-secondary font-mono">Loading projects...</span>
                    </div>
                )}

                {/* Error */}
                {isError && (
                    <div className="rounded-xl border border-warning/30 bg-warning/5 p-6 text-center">
                        <p className="text-text-secondary font-mono">
                            ⚠️ Could not load projects from the server.
                        </p>
                    </div>
                )}

                {/* Projects Grid/List */}
                {!isLoading && filteredProjects.length === 0 ? (
                    <Card padding="lg" className="text-center">
                        <FolderOpen className="h-16 w-16 text-text-muted mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-text-primary font-mono mb-2">
                            {t('projects.noProjects')}
                        </h3>
                        <p className="text-text-muted font-mono mb-6">
                            {searchQuery
                                ? t('projects.noProjectsSearch')
                                : t('projects.noProjectsEmpty')}
                        </p>
                        {!searchQuery && (
                            <Button variant="primary" corners="asymmetric" onClick={handleCreateProject}>
                                {t('projects.createProject')}
                            </Button>
                        )}
                    </Card>
                ) : (
                    <div
                        className={
                            viewMode === 'grid'
                                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                                : 'space-y-4'
                        }
                    >
                        {filteredProjects.map((project) => (
                            <ProjectCard
                                key={project.id}
                                project={project as any}
                                viewMode={viewMode}
                                onEdit={() => handleEditProject(project as any)}
                                onDelete={() => handleDeleteProject(project.id, project.name)}
                                onClick={() => handleProjectClick(project.id)}
                            />
                        ))}
                    </div>
                )}
            </main>

            {/* Create/Edit Project Modal */}
            <ProjectForm
                isOpen={isCreateModalOpen}
                onClose={() => {
                    setIsCreateModalOpen(false);
                    // Show toast only when creating a new project (not editing)
                    if (!selectedProject) {
                        sileo.success({
                            title: t('toasts.projectCreated'),
                            description: t('toasts.projectCreatedDesc'),
                            icon: <FolderPlus className="size-3.5" />,
                        });
                    }
                }}
                project={selectedProject}
            />
        </div>
    );
}
